import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Mail, Phone, MessageCircle } from "lucide-react";
import PhoneInput from "@/components/common/PhoneInput";
import useSiteConfig, { whatsappLinkFromCfg } from "@/lib/useSiteConfig";
import { track } from "@/components/common/Analytics";

export default function Contact() {
  const cfg = useSiteConfig();
  const supportEmail = cfg.support_email || "stay@vyagram.com";
  const phoneTel = `+${(cfg.whatsapp_number || "919355012999").replace(/[^\d]/g, "")}`;
  const waLink = whatsappLinkFromCfg(cfg, "Hi Vyagram, I'd like to plan a stay.");
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", form);
      track("generate_lead", { source: "contact_form", content_category: "enquiry" });
      toast.success("Thanks — we'll be in touch shortly.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="contact-page" className="mx-auto w-full max-w-6xl overflow-x-hidden px-4 py-14 sm:px-6 lg:px-10">
      <Helmet><title>Contact · Vyagram Experiences</title></Helmet>
      <div className="grid gap-12 lg:grid-cols-2">
        <div>
          <p className="vyg-eyebrow">Get in touch</p>
          <h1 className="mt-3 font-display text-4xl sm:text-5xl">Plan your stay.</h1>
          <p className="editorial-body mt-4">Our hospitality team usually replies within a few hours, often faster.
          Tell us what you're planning and we'll help shape the stay around it.</p>
          <div className="mt-8 space-y-4 text-sm">
            <a href={`mailto:${supportEmail}`} className="flex items-center gap-3 rounded-xl border border-forest/15 bg-white p-4 hover:bg-mist/60" data-testid="contact-email"><Mail className="text-forest" size={18}/> {supportEmail}</a>
            <a href={`tel:${phoneTel}`} className="flex items-center gap-3 rounded-xl border border-forest/15 bg-white p-4 hover:bg-mist/60" data-testid="contact-phone"><Phone className="text-forest" size={18}/> {cfg.whatsapp_display}</a>
            <a href={waLink} target="_blank" rel="noreferrer" className="flex items-center gap-3 rounded-xl bg-[#25D366] p-4 text-white hover:opacity-95" data-testid="contact-whatsapp"><MessageCircle size={18}/> WhatsApp us at {cfg.whatsapp_display}</a>
          </div>
        </div>

        <form onSubmit={submit} className="rounded-2xl border border-forest/15 bg-white p-6 shadow-card sm:p-8">
          <h2 className="font-display text-2xl">Send us a note</h2>
          <div className="mt-5 grid gap-4">
            <Field label="Your name" testid="contact-name">
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full rounded-lg border border-forest/20 bg-parchment/60 px-4 py-3 focus:border-forest focus:outline-none" data-testid="contact-name-input" />
            </Field>
            <Field label="Email" testid="contact-email-field">
              <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full rounded-lg border border-forest/20 bg-parchment/60 px-4 py-3 focus:border-forest focus:outline-none" data-testid="contact-email-input" />
            </Field>
            <Field label="Phone (optional)" testid="contact-phone-field">
              <PhoneInput value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="contact-phone-input" />
            </Field>
            <Field label="Tell us about your stay" testid="contact-message-field">
              <textarea required rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="w-full rounded-lg border border-forest/20 bg-parchment/60 px-4 py-3 focus:border-forest focus:outline-none" data-testid="contact-message-input" />
            </Field>
            <button disabled={sending} type="submit" className="vyg-cta mt-2" data-testid="contact-submit">{sending ? "Sending…" : "Send"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-wider text-amber">{label}</span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}
